import { firestore } from "./Init";
import {
  doc,
  onSnapshot,
  getDoc,
  updateDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import {
  isUsernameTaken,
  reserveUsername,
  releaseUsername,
} from "./Username";
import { validateUser } from "./schema";

// Listen for changes to the user document.
//
// `callback(null)` means RESOLVED-AND-MISSING, not "still loading" — AuthGate
// now distinguishes the two and routes a missing doc to UserDocRecovery.
//
// onError exists so a terminal listener failure still resolves the loading
// state. Firestore does not retry a listener error, so without it `loading`
// stays true forever and the user sits on a full-screen spinner until reload —
// the same bricking symptom by a different route. The last-delivered doc is
// deliberately NOT overwritten: stale data leaves the app usable, and the
// server re-enforces every gate anyway.
export const fetchUserData = (uid, callback, onError) => {
  if (!uid) return null;

  const userRef = doc(firestore, "users", uid);
  return onSnapshot(
    userRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      const data = snapshot.data();
      callback({ ...data, uid: snapshot.id });
    },
    (error) => {
      console.error("Error listening to user document:", error);
      if (typeof onError === "function") onError(error);
    }
  );
};

// Update user data (including handling username changes)
export const updateUserData = async (uid, updates) => {
  if (!uid) return;

  let oldUsername = null;
  let newUsername = null;

  try {
    const userRef = doc(firestore, "users", uid);
    const snapshot = await getDoc(userRef);
    const currentData = snapshot.data() || {};

    const mergedData = {
      ...currentData,
      ...updates,
      lastUpdated: new Date().toISOString(),
    };

    // Handle username change if applicable
    if (mergedData.username && mergedData.username !== currentData.username) {
      newUsername = mergedData.username;
      oldUsername = currentData.username || null;

      const usernameDocRef = doc(
        firestore,
        "usernames",
        newUsername.toLowerCase().trim()
      );
      const usernameSnapshot = await getDoc(usernameDocRef);
      const usernameData = usernameSnapshot.data();

      if (!usernameData || usernameData.uid !== uid) {
        const taken = await isUsernameTaken(newUsername);
        if (taken) {
          throw new Error("Username already taken");
        }

        const reserved = await reserveUsername(newUsername, uid);
        if (!reserved) {
          throw new Error("Failed to reserve new username");
        }
      }
    }

    await setDoc(userRef, mergedData, { merge: true });

    if (oldUsername && newUsername) {
      await releaseUsername(oldUsername);
    }
  } catch (error) {
    if (newUsername) {
      await releaseUsername(newUsername);
    }
    console.error("Error updating user data:", error);
    throw error;
  }
};

// Mark a user's age attestation. Phase 0 legal gate.
export const setUserAgeAttested = async (uid, attested = true) => {
  if (!uid) return;
  const userRef = doc(firestore, "users", uid);
  await updateDoc(userRef, {
    age_attested: !!attested,
    lastUpdated: new Date().toISOString(),
  });
};

// Fetch a user by their username
export const fetchUserByUsername = async (username) => {
  if (!username || typeof username !== "string") {
    return null;
  }
  const sanitizedUsername = username.toLowerCase().trim();
  try {
    const usersRef = collection(firestore, "users");
    const q = query(usersRef, where("username", "==", sanitizedUsername));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();

    return {
      uid: userDoc.id,
      username: userData.username || "",
      display_name: userData.display_name || null,
    };
  } catch (error) {
    console.error("Error fetching user by username:", error);
    throw error;
  }
};

// Create the user document in Firestore.
export const createUserDocument = async (user, additionalData = {}) => {
  if (!user?.uid) {
    throw new Error("Invalid user object");
  }

  try {
    const userRef = doc(firestore, "users", user.uid);
    const userData = {
      uid: user.uid,
      email: user.email || additionalData.email || "",
      username: additionalData.username || null,
      display_name: additionalData.display_name || user.displayName || null,
      // serverTimestamp, NOT an ISO string. This was a string until the reminder
      // cron was found to be reaching zero users: it queries
      // `where("created_at","<", Timestamp)`, and Firestore orders all Timestamps
      // before all Strings, so no string-valued doc ever matched. See the header
      // of functions/scheduledFunctions/sendInactivityReminders.js.
      created_at: serverTimestamp(),
      age_attested: false,
      legacy_migrated: false,
      onboarding_complete: false,
      tutorial_complete: false,
      ...additionalData,
    };

    const errors = validateUser(userData);
    if (errors) {
      throw new Error(`User validation failed: ${JSON.stringify(errors)}`);
    }

    await setDoc(userRef, userData, { merge: true });
    return userRef;
  } catch (error) {
    // No cleanup delete here. There used to be a cleanupFailedUserCreation()
    // that deleted users/{uid} on ANY error — including a validateUser failure
    // that happens before a single byte is written. A single setDoc leaves no
    // partial state to clean, and now that onAuthUserCreate provisions the doc
    // server-side, that delete could destroy a perfectly good server-created doc.
    console.error("Error in createUserDocument:", error);
    throw error;
  }
};

// NOTE: there is deliberately no `lastSignInTime` mirror here. See the comment
// on doSignInWithEmailAndPassword in Auth.js — the field was never in the
// firestore.rules users-update allow-list, so the write was always rejected and
// the rejection broke sign-in. Firebase Auth already owns this data
// (user.metadata.lastSignInTime client-side, lastLoginAt/lastRefreshAt via the
// Admin SDK). Do not reintroduce it; firestoreRules.test.js asserts it is absent.

// Set username (reserves it as well).
export const setUserUsername = async (uid, username) => {
  if (!uid || !username) {
    throw new Error("User ID and username are required");
  }

  try {
    const taken = await isUsernameTaken(username);
    if (taken) {
      throw new Error("Username already taken");
    }

    const reserved = await reserveUsername(username, uid);
    if (!reserved) {
      throw new Error("Failed to reserve username");
    }

    const userRef = doc(firestore, "users", uid);
    await updateDoc(userRef, {
      username,
      lastUpdated: new Date().toISOString(),
    });

    return true;
  } catch (error) {
    console.error("Error setting username:", error);
    await releaseUsername(username);
    throw error;
  }
};

// Fetch a user document by uid.
export const getUserById = async (uid) => {
  if (!uid) return null;
  try {
    const userRef = doc(firestore, "users", uid);
    const snapshot = await getDoc(userRef);
    if (!snapshot.exists()) return null;
    return { uid: snapshot.id, ...snapshot.data() };
  } catch (error) {
    console.error("Error fetching user:", error);
    return null;
  }
};
